import React from 'react'
import clsx from 'clsx'
import { useAppSelector } from '@SonarCX/hooks/redux'
import {
  ArrowLeft,
  ArrowSwapHorizontal,
  CloseCircle,
  LogoutCurve,
  ReceiveSquare
} from 'iconsax-react'
import { Fade, Menu, useTheme } from '@mui/material'

import { MobileScreen, setMobileScreen } from '@SonarCX/slices/convo'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { selectPresence } from '@SonarCX/slices/presence'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAppDispatch } from '@SonarCX/hooks/redux'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { getTooltipInfo } from '../../ChatWindow/Header/AvatarSection/TooltipDetails'

import AddParticipantsButton from '../../../atoms/AddParticipantsButton'
import Tooltip from '../../../atoms/Tooltip'
import Participants from '../Participants'
import MediaAvatar from '../../../atoms/MediaAvatar'
import ButtonMenu from '../../../molecules/ButtonMenu2'
import MoreHorizontalIcon from '../../../icons/MoreHorizontalIcon'
import ParticipantsNames from '../ParticipantsNames'
import ConversationDownloadModal from './ConversationDownloadModal'
import EditParticipantsModal from './EditParticipantsModal'
import TransferConversationModal from './TransferModal'
import Card from '../../../atoms/MediaAvatar/Card'

import { setAlertsHidden } from '@SonarCX/slices/chatBoxSlice'
import useStyles from './styles'
import FavoriteButton from '@SonarCX/components/atoms/FavoriteButton'
import useFavorites from '@SonarCX/hooks/useFavorites'

const TransferIcon = () => (
  <ArrowSwapHorizontal
    size='14'
    color='#8F8F8F'
    variant='Bold'
    style={{ marginRight: 8 }}
  />
)

const DownloadIcon = () => (
  <ReceiveSquare
    size='14'
    color='#8F8F8F'
    variant='Bold'
    style={{ marginRight: 8 }}
  />
)

const EndIcon = () => (
  <LogoutCurve
    size='14'
    color='#8F8F8F'
    variant='Bold'
    style={{ transform: 'rotate(180deg)', marginRight: 8 }}
  />
)

const CloseIcon = () => (
  <CloseCircle
    size='14'
    color='#8F8F8F'
    variant='Bold'
    style={{ marginRight: 8 }}
  />
)

const OPTS = {
  transfer: 'Transfer Conversation',
  download: 'Download Conversation',
  end: 'End Conversation',
  close: 'Close Conversation'
}

const Header = ({ conversation, endSession }) => {
  const dispatch = useAppDispatch()
  const theme = useTheme()

  const { currentUser } = useAgent()
  const { allUsers, loadUser } = useUserAPI()
  const { presence } = useAppSelector(selectPresence)
  const { favoriteSessions } = useFavorites()
  const { isSmall } = useBreakpoint()
  const { handleSelect: selectConvo, id: selectedConversation } =
    useQueueState()
  const {
    api: { closeSession }
  } = useSocket()

  const [downloading, setDownloading] = React.useState(false)
  const [editingParticipants, setEditingParticipants] = React.useState(false)
  const [transfering, setTransfering] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [anchor, setAnchor] = React.useState(null)
  const [cardUser, setCardUser] = React.useState(null)
  const me = React.useMemo(() => currentUser(), [currentUser])
  const convo = React.useMemo(
    () => (conversation?.owner_id ? conversation : {}),
    [conversation]
  )
  const isFavorite = React.useMemo(() => {
    if (!convo?.session) return false

    return favoriteSessions.some(x => x.item_id === convo.session)
  }, [convo, favoriteSessions])

  const userIds = React.useMemo(
    () => convo?.userIds?.filter(u => u !== me?.id) || [],
    [convo, me]
  )
  const users = React.useMemo(() => {
    if (!userIds) {
      return []
    }

    const users = []

    userIds.forEach(id => {
      const user = allUsers[id]
      if (user && user.name) {
        users.push(user)
      }
    })

    setCardUser(users[0])
    return users
  }, [userIds, allUsers])

  //only display the owner on mobile devices
  const participantsToRender = isSmall
    ? [convo?.owner_id]
    : [...userIds].slice(0, 3)
  const isGroupChat = isSmall
    ? [...userIds].length >= 2
    : participantsToRender.length >= 2

  const options = React.useMemo(() => {
    const opts = [
      {
        name: OPTS.transfer,
        Icon: TransferIcon
      },
      {
        name: OPTS.download,
        Icon: DownloadIcon
      }
    ]

    if (convo.state === 'ENDED') {
      opts.push({
        name: OPTS.close,
        Icon: CloseIcon
      })
    } else if (convo.state !== 'CLOSED') {
      opts.push({
        name: OPTS.end,
        Icon: EndIcon
      })
    }

    return opts
  }, [convo])

  const handleSelect = React.useCallback(
    val => {
      switch (val) {
        case OPTS.end:
          endSession()
          break
        case OPTS.transfer:
          setTransfering(true)
          break
        case OPTS.download:
          setDownloading(true)
          break
        case OPTS.close:
          closeSession(selectedConversation)
          break
        default:
          break
      }
    },
    [endSession, closeSession, selectedConversation]
  )

  const handleCloseDownloadModal = React.useCallback(() => {
    setDownloading(false)
  }, [])

  const handleCloseTransferModal = React.useCallback(() => {
    setTransfering(false)
  }, [])

  const handleOpenParticipantsModal = React.useCallback(
    e => {
      e.stopPropagation()
      setEditingParticipants(true)
      if (isSmall) {
        dispatch(setAlertsHidden(true))
      }
    },
    [isSmall, dispatch]
  )

  const handleCloseParticipantsModal = React.useCallback(() => {
    setEditingParticipants(false)
    dispatch(setAlertsHidden(false))
  }, [dispatch])

  const handleParticipantsClick = React.useCallback(
    e => {
      if (!isGroupChat) return
      e.stopPropagation()
      setAnchor(e.currentTarget)
      setOpen(true)
    },
    [isGroupChat]
  )

  const handleCloseCard = React.useCallback(e => {
    e.stopPropagation()
    setAnchor(null)
    setOpen(false)
  }, [])

  const onRowClick = React.useCallback(u => {
    setCardUser(u)
  }, [])

  const handleOnHeaderClick = React.useCallback(
    e => {
      if (isSmall) {
        e.preventDefault()
        dispatch(setMobileScreen(MobileScreen.EngagementHub))
      }
    },
    [isSmall, dispatch]
  )

  const handleReturnClick = React.useCallback(
    e => {
      e.stopPropagation()
      dispatch(setMobileScreen(MobileScreen.Queue))
      selectConvo(null)
    },
    [dispatch, selectConvo]
  )

  const classes = useStyles()

  const position = { top: 0, left: 0 }
  if (anchor) {
    const bounds = anchor.getBoundingClientRect()
    position.top = bounds.bottom + 6
    position.left = bounds.left + 28
  }

  return (
    <>
      <div className={classes.root} onClick={handleOnHeaderClick}>
        <section
          className={clsx(
            classes.flexAlignCenter,
            classes.flex2,
            classes.sectionContainer,
            classes.nameSectionContainer
          )}
        >
          <span className={classes.flexAlignCenter}>
            {isSmall && (
              <ArrowLeft
                size={theme.size.xl}
                onClick={handleReturnClick}
                style={{ marginRight: 8 }}
              />
            )}
            <div
              className={clsx(
                classes.flexAlignCenter,
                isGroupChat && classes.pointer
              )}
              onClick={handleParticipantsClick}
              style={{ cursor: 'pointer' }}
            >
              {participantsToRender.map((userid, index) => (
                <Participants
                  key={userid}
                  userId={userid}
                  index={index}
                  numberOfParticipants={participantsToRender.length}
                  allUsers={allUsers}
                  loadUser={loadUser}
                  isGroupChat={isGroupChat}
                />
              ))}
              {isGroupChat && open && (
                <Menu
                  open
                  anchorEl={anchor}
                  getContentAnchorEl={null}
                  anchorReference={anchor ? 'anchorPosition' : 'anchorEl'}
                  anchorPosition={position}
                  onClose={handleCloseCard}
                  TransitionComponent={Fade}
                  classes={{
                    paper: classes.menuPaper,
                    list: classes.menuList
                  }}
                >
                  <Card
                    user={cardUser}
                    close={handleCloseCard}
                    multi
                    users={users}
                    onUsersRowClick={onRowClick}
                  />
                </Menu>
              )}
            </div>

            <Tooltip title={getTooltipInfo(allUsers, userIds, presence)}>
              <div className={classes.avatarNames}>
                <ParticipantsNames
                  userIds={userIds}
                  owner={convo?.owner_id}
                  allUsers={allUsers}
                  isSmall={isSmall}
                />
              </div>
            </Tooltip>
          </span>
          <div
            className={clsx(
              classes.flexAlignCenter,
              classes.addMoreParticipants
            )}
          >
            <Tooltip
              title={'Add/Remove participant'}
              placement='right-start'
              classes={{ tooltipPlacementRight: classes.addTooltip }}
            >
              <div
                onClick={handleOpenParticipantsModal}
                className={classes.flexAlignCenter}
              >
                <AddParticipantsButton />
              </div>
            </Tooltip>
          </div>
        </section>

        <section
          className={clsx(
            classes.flexAlignCenter,
            classes.rightSection,
            classes.sectionContainer
          )}
        >
          <FavoriteButton
            isFavorite={isFavorite}
            itemId={convo?.session}
            type='session'
            additionalClass={classes.favBtn}
          />
          {!isSmall && <MediaAvatar group showBadge user={me} />}
          <span className={classes.myDetails}>
            <div className={classes.me}>You</div>
            {/* <div>Team</div> */}
          </span>
          {!convo?.closed && (
            <div
              data-testid='header-end-convo'
              className={classes.endConversation}
            >
              <Tooltip title={'End Conversation'} arrow>
                <span
                  onClick={e => {
                    e.stopPropagation()
                    endSession()
                  }}
                >
                  <LogoutCurve size='24' color='#404040' variant='Bold' />
                </span>
              </Tooltip>
            </div>
          )}
          <span>
            <ButtonMenu options={options} onSelect={handleSelect}>
              <MoreHorizontalIcon />
            </ButtonMenu>
          </span>
        </section>
      </div>
      {downloading && (
        <ConversationDownloadModal
          open={downloading}
          onClose={handleCloseDownloadModal}
        />
      )}

      {editingParticipants && (
        <EditParticipantsModal
          mobile={isSmall}
          onClose={handleCloseParticipantsModal}
        />
      )}

      {transfering && (
        <TransferConversationModal
          owner={convo?.owner_id}
          conversation={convo}
          onClose={handleCloseTransferModal}
          allUsers={allUsers}
        />
      )}
    </>
  )
}

export default Header
